/**
 * `useConfirm()` — the app's replacement for the browser's blocking confirm dialog.
 *
 * `const ok = await confirm({ title, body, confirmLabel, tone })` opens one
 * dialog (`ConfirmProvider`, mounted once in `main.tsx`) and resolves with
 * the operator's answer. `tone: "danger"` puts initial focus on Cancel, so a
 * reflexive Enter never confirms something irreversible; the default tone
 * focuses Confirm, so keyboard triage can approve with Enter.
 *
 * Use it for actions that cannot be taken back. A reversible action gets an
 * Undo toast instead (`useToast().show(msg, { action })`).
 */
import { createContext, useContext, type ReactNode } from "react";

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm() needs <ConfirmProvider> above it");
  return confirm;
}
